<script setup lang="ts">
import type { RangeCalendarCellTriggerProps } from "reka-ui"
import type { HTMLAttributes } from "vue"
import { reactiveOmit } from "@vueuse/core"
import { RangeCalendarCellTrigger, useForwardProps } from "reka-ui"
import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

const props = defineProps<RangeCalendarCellTriggerProps & { class?: HTMLAttributes["class"] }>()

const delegatedProps = reactiveOmit(props, "class")

const forwardedProps = useForwardProps(delegatedProps)
</script>

<template>
  <RangeCalendarCellTrigger
    :class="cn(
      buttonVariants({ variant: 'ghost' }),
      'relative h-9 w-9 whitespace-nowrap p-0 font-normal',
      '[&[data-today]:not([data-selected])]:bg-accent [&[data-today]:not([data-selected])]:text-accent-foreground',
      // Reka marca TODOS los días del rango con `data-selected`, no solo los
      // extremos. Base sólida...
      'data-[selected]:bg-primary data-[selected]:text-primary-foreground data-[selected]:opacity-100 data-[selected]:hover:bg-primary data-[selected]:hover:text-primary-foreground data-[selected]:focus:bg-primary data-[selected]:focus:text-primary-foreground',
      // ...y los de en medio se bajan a un tono suave, para que se lean como el
      // tramo entre entrega y devolución y no como cinco fechas elegidas.
      //
      // Va como variante arbitraria con dos `:not()` a propósito: `data-[selected]`
      // y `data-[selection-start]` tienen la MISMA especificidad, así que cuál gana
      // dependería del orden en que Tailwind emita las reglas. Con los `:not()` la
      // especificidad es mayor y el resultado deja de ser cuestión de suerte.
      '[&[data-selected]:not([data-selection-start]):not([data-selection-end])]:rounded-none [&[data-selected]:not([data-selection-start]):not([data-selection-end])]:bg-accent [&[data-selected]:not([data-selection-start]):not([data-selection-end])]:text-accent-foreground',
      // Preview mientras se arrastra el rango.
      'data-[highlighted]:rounded-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
      'data-[selection-start]:rounded-l-md data-[selection-end]:rounded-r-md',
      'data-[outside-view]:text-muted-foreground data-[outside-view]:opacity-50',
      'data-[disabled]:pointer-events-none data-[disabled]:text-muted-foreground data-[disabled]:opacity-50',
      'data-[unavailable]:pointer-events-none data-[unavailable]:text-muted-foreground data-[unavailable]:line-through',
      props.class,
    )"
    v-bind="forwardedProps"
  >
    <slot>
      {{ props.day.day }}
    </slot>
  </RangeCalendarCellTrigger>
</template>
